import { create } from 'zustand'
import { apiClient } from '@api'

/**
 * Notification Store
 *
 * Zustand store for managing user notifications.
 * Handles fetching, marking as read, and polling for new notifications.
 */
export const useNotificationStore = create((set, get) => ({
	// ====================================================================
	// STATE
	// ====================================================================

	notifications: [],
	unreadCount: 0,
	loading: false,
	error: null,
	pollingInterval: null,

	// ====================================================================
	// ACTIONS
	// ====================================================================

	/**
	 * Fetch notifications for current user
	 * @param {boolean} unreadOnly - Only fetch unread notifications
	 */
	fetchNotifications: async (unreadOnly = false) => {
		set({ loading: true, error: null })
		try {
			const endpoint = unreadOnly
				? '/notifications?unread=true'
				: '/notifications'
			const response = await apiClient.get(endpoint)
			const notifications = response.data || []

			set({
				notifications,
				unreadCount: notifications.filter(n => !n.read_at).length,
				loading: false
			})
		} catch (error) {
			console.error('Failed to fetch notifications:', error)
			set({ error: error.message, loading: false })
		}
	},

	/**
	 * Get unread count only (lightweight)
	 */
	fetchUnreadCount: async () => {
		try {
			const response = await apiClient.get('/notifications/unread-count')
			set({ unreadCount: response.data?.count || 0 })
		} catch (error) {
			console.error('Failed to fetch unread count:', error)
		}
	},

	/**
	 * Mark a single notification as read
	 * @param {number} id - Notification ID
	 */
	markAsRead: async (id) => {
		try {
			await apiClient.patch(`/notifications/${id}/read`)
			set(state => ({
				notifications: state.notifications.map(n =>
					n.id === id ? { ...n, read_at: new Date().toISOString() } : n
				),
				unreadCount: Math.max(0, state.unreadCount - 1)
			}))
		} catch (error) {
			console.error('Failed to mark notification as read:', error)
		}
	},

	/**
	 * Mark all notifications as read
	 */
	markAllAsRead: async () => {
		try {
			await apiClient.patch('/notifications/read-all')
			set(state => ({
				notifications: state.notifications.map(n => ({
					...n,
					read_at: n.read_at || new Date().toISOString()
				})),
				unreadCount: 0
			}))
		} catch (error) {
			console.error('Failed to mark all as read:', error)
		}
	},

	/**
	 * Add a new notification (for real-time updates)
	 * @param {Object} notification - Notification object
	 */
	addNotification: (notification) => {
		set(state => ({
			notifications: [notification, ...state.notifications],
			unreadCount: state.unreadCount + 1
		}))
	},

	/**
	 * Delete a notification and keep unread count accurate
	 * @param {number} id - Notification ID
	 * @param {boolean} wasUnread - Whether the notification was unread
	 */
	deleteNotification: (id, wasUnread) => {
		set(state => ({
			notifications: state.notifications.filter(n => n.id !== id),
			unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
		}))
	},

	/**
	 * Start polling for new notifications
	 * @param {number} intervalMs - Polling interval in milliseconds
	 * @returns {Function} Cleanup function to stop polling
	 */
	startPolling: (intervalMs = 30000) => {
		const { fetchNotifications, pollingInterval } = get()

		// Clear existing interval
		if (pollingInterval) {
			clearInterval(pollingInterval)
		}

		// Fetch immediately
		fetchNotifications()

		// Set up polling
		const interval = setInterval(() => {
			fetchNotifications()
		}, intervalMs)

		set({ pollingInterval: interval })

		// Return cleanup function
		return () => {
			clearInterval(interval)
			set({ pollingInterval: null })
		}
	},

	/**
	 * Stop polling for notifications
	 */
	stopPolling: () => {
		const { pollingInterval } = get()
		if (pollingInterval) {
			clearInterval(pollingInterval)
			set({ pollingInterval: null })
		}
	},

	/**
	 * Clear error state
	 */
	clearError: () => {
		set({ error: null })
	},

	/**
	 * Reset store to initial state
	 */
	reset: () => {
		const { pollingInterval } = get()
		if (pollingInterval) {
			clearInterval(pollingInterval)
		}

		set({
			notifications: [],
			unreadCount: 0,
			loading: false,
			error: null,
			pollingInterval: null
		})
	}
}))

export default useNotificationStore
